import type { Node as NvlNode, Relationship as NvlRelationship } from '@neo4j-ndl/react/nvl';
import { DEFAULT_RELATIONSHIP_COLOR } from '@nx/state';
import { createSelector } from '@reduxjs/toolkit';
import { isNil } from 'lodash';
import { cloneDeep, isEmpty, keyBy } from 'lodash-es';

import { fullPallete } from '../../modules/Legend/Popups/RuleBasedStyling/colorHelpers';
import { getTitleFromRuleBase } from '../../modules/Legend/Popups/RuleBasedStyling/rules';
import type { Category } from '../../types/category';
import { type Node as BloomNode, type Relationship as BloomRelationship } from '../../types/graph';
import type {
  CategoryWithStyle,
  Perspective,
  PerspectiveRelationshipType,
  PerspectiveWithStyle,
  RelationshipTypeWithStyle,
} from '../../types/perspective';
import type { Style, StyleCategory, StyleRelationshipType, StyleRule } from '../../types/style';
import type { NonUndefined, Nullable } from '../../types/utility';
import { isVirtualRelationshipFactory } from '../graph/relationships';
import { DEFAULT_UNCATEGORISED_ID } from '../perspectives/constants';
import {
  getAllSortedPerspectives,
  getCategories,
  getCategoryByName,
  getCategoryForNode,
  getRelationshipDetails,
  getRelationshipTypes,
} from '../perspectives/perspectives';
import { getPerspective, getPerspectiveById } from '../perspectives/perspectives.selector';
import {
  getRuleBasedCaptions,
  getRuleBasedColor,
  getRuleBasedSize,
  getRuleBasedTextAlign,
  getRuleBasedTextSize,
} from '../perspectives/ruleEvaluators';
import type { RootState } from '../types';
import { findCaptionProperty } from './styleMiddleware.utils';
import type { StyleState } from './styles';
import { getOtherCategory } from './styles';
import {
  COLOR_MAPPING_THRESHOLD,
  DEFAULT_RELATIONSHIP_SIZE,
  DEFAULT_UNCATEGORISED_COLOR,
  DEFAULT_UNCATEGORISED_SIZE,
  DEFAULT_UNCATEGORISED_TEXT_ALIGN,
  DEFAULT_UNCATEGORISED_TEXT_ALIGN_RELATIONSHIP,
  DEFAULT_UNCATEGORISED_TEXT_SIZE,
  NAME,
} from './styles.const';
import type { Caption, CaptionStyle } from './types';

export const selectCurrentStyle = (state: RootState): Style | undefined =>
  !isNil(state[NAME].currentStyleId) ? state[NAME].styles[state[NAME].currentStyleId] : undefined;

export const selectStyleById = createSelector(
  (state: RootState) => state[NAME].styles,
  (styles) => (id: Style['id'] | undefined) => (!isNil(id) ? styles?.[id] : undefined),
);

export const selectStyleType = (state: RootState): StyleState['currentStyleType'] => state[NAME].currentStyleType;

const mergePerspectiveWithStyle =
  (state: RootState) =>
  (perspectiveId?: Perspective['id'], styleId?: Style['id']): Nullable<PerspectiveWithStyle> => {
    const perspective = !isNil(perspectiveId) ? getPerspectiveById(state)(perspectiveId) : getPerspective(state);

    if (isNil(perspective)) {
      return null;
    }

    const style = selectStyleById(state)(styleId ?? perspective.id);

    if (isNil(style)) {
      return null;
    }
    const perspectiveWithStyle = {
      ...perspective,
      palette: style.palette,
    };

    if (!isNil(perspective.categories)) {
      const styleCat = !isNil(style.categories) ? keyBy(style.categories, 'id') : {};
      perspectiveWithStyle.categories = perspective.categories.map<CategoryWithStyle>((cat) => ({
        ...cat,
        ...(styleCat[cat.id] ?? getOtherCategory()),
      }));
    }

    if (!isNil(perspective.relationshipTypes)) {
      const styleRelTypes = !isNil(style.relationshipTypes) ? keyBy(style.relationshipTypes, 'id') : {};
      perspectiveWithStyle.relationshipTypes = perspective.relationshipTypes?.map(
        (relType: PerspectiveRelationshipType) => Object.assign({}, relType, styleRelTypes[relType.id]),
      );
    }

    return perspectiveWithStyle as PerspectiveWithStyle;
  };

export const selectPerspectiveWithStyle = createSelector(
  mergePerspectiveWithStyle,
  (mapper) => (perspectiveId?: Perspective['id']) => mapper(perspectiveId),
);

export const selectPerspectiveWithCurrentStyle = createSelector(
  selectCurrentStyle,
  mergePerspectiveWithStyle,
  (style, mapper) => mapper(undefined, style?.id),
);

export const selectCategoryStyleForNode = createSelector(
  selectCurrentStyle,
  getCategoryForNode,
  (style, mapper) =>
    (node: BloomNode): StyleCategory => {
      const cat = mapper(node);
      return (
        style?.categories?.find(({ id }) => id === cat.id) ?? { ...getOtherCategory(), id: DEFAULT_UNCATEGORISED_ID }
      );
    },
);

export const selectCategoriesWithPerspectiveStyle = createSelector(
  getPerspective,
  selectStyleById,
  (perspective, selectStyleById) => {
    if (isNil(perspective)) {
      return [];
    }

    const style = selectStyleById(perspective.id);

    const perspCategories = perspective.categories;
    const styleCat = keyBy(style?.categories, 'id');

    return perspCategories?.map((cat) => ({
      ...cat,
      ...(styleCat[cat.id] ?? getOtherCategory()),
    }));
  },
);

export const selectCategoriesWithCurrentStyle = createSelector(
  getCategories,
  selectCurrentStyle,
  selectCategoriesWithPerspectiveStyle,
  (categories, style, categoriesWithPerspectiveStyle): CategoryWithStyle[] => {
    if (isNil(style)) {
      return categoriesWithPerspectiveStyle;
    }

    const perspCategories: Perspective['categories'] = cloneDeep(categories);
    const styleCat = keyBy(style.categories, 'id');

    return perspCategories?.map((cat) => ({
      ...cat,
      ...(styleCat[cat.id] ?? getOtherCategory()),
    }));
  },
);

export const selectRelationshipTypesWithPerspectiveStyle = createSelector(
  getPerspective,
  selectStyleById,
  getRelationshipDetails,
  (perspective, selectStyleById, perspRelTypes): RelationshipTypeWithStyle[] => {
    if (isNil(perspective)) {
      return [];
    }

    const style = selectStyleById(perspective.id);
    if (isNil(style)) {
      return [];
    }

    const styleRelTypes = keyBy(style.relationshipTypes, 'id');

    return perspRelTypes.map((relType: PerspectiveRelationshipType) => ({
      ...relType,
      ...(styleRelTypes[relType.id] as StyleRelationshipType),
    }));
  },
);

export const selectRelationshipTypesWithCurrentStyle = createSelector(
  selectCurrentStyle,
  selectRelationshipTypesWithPerspectiveStyle,
  getRelationshipDetails,
  (style, relationshipTypesWithPerspectiveStyle, perspRelTypes): RelationshipTypeWithStyle[] => {
    if (isNil(style)) {
      return relationshipTypesWithPerspectiveStyle;
    }

    const styleRelTypes = keyBy(style.relationshipTypes, 'id');

    return perspRelTypes.map((relType: PerspectiveRelationshipType) => ({
      ...relType,
      ...(styleRelTypes[relType.id] as StyleRelationshipType),
    }));
  },
);

export const selectRelationshipTypesWithCurrentStyleMap = createSelector(
  selectCurrentStyle,
  selectRelationshipTypesWithPerspectiveStyle,
  getRelationshipDetails,
  (style, relationshipTypesWithPerspectiveStyle, perspRelTypes): any => {
    if (isNil(style)) {
      return relationshipTypesWithPerspectiveStyle;
    }

    const styleRelTypes = keyBy(style.relationshipTypes, 'id');

    const relTypeMap: Record<string, StyleRelationshipType> = {};

    perspRelTypes.forEach((relType: PerspectiveRelationshipType) => {
      relTypeMap[relType.name] = {
        ...relType,
        ...(styleRelTypes[relType.id] as StyleRelationshipType),
      };
    });

    return relTypeMap;
  },
);

export const selectAllSortedPerspectivesWithStyles = createSelector(
  getAllSortedPerspectives,
  selectPerspectiveWithStyle,
  // @ts-expect-error perspective slice is not converted yet
  (perspectives, mapper) => perspectives.map((persp) => mapper(persp.id)),
);

export const selectColorForRelationshipTypeByName = createSelector(
  selectCurrentStyle,
  selectRelationshipTypesWithCurrentStyleMap,
  (style, relTypeMapper) => (relTypeName: RelationshipTypeWithStyle['name']) => {
    if (isNil(style)) return DEFAULT_UNCATEGORISED_COLOR;
    const styleRelType = style?.relationshipTypes?.find(({ id }) => id === relTypeMapper[relTypeName]?.id);
    return styleRelType?.color;
  },
);

export const selectColorForRelMapper = createSelector(
  selectCurrentStyle,
  (style) =>
    (rel: BloomRelationship): NonUndefined<NvlRelationship['color']> => {
      if (isNil(style) || isNil(style?.relationshipTypes)) {
        return DEFAULT_RELATIONSHIP_COLOR;
      }
      const relTypeDetails = style.relationshipTypes.find(({ id }) => id === rel.type) ?? {
        color: DEFAULT_RELATIONSHIP_COLOR,
        styleRules: undefined,
      };
      const color = relTypeDetails?.color ?? DEFAULT_RELATIONSHIP_COLOR;
      const override = getRuleBasedColor(relTypeDetails?.styleRules, rel) as string | undefined;
      return override ?? color;
    },
);

export const selectSizeForRelMapper = createSelector(
  selectCurrentStyle,
  (style) =>
    (rel: BloomRelationship): NvlRelationship['width'] => {
      if (isNil(style?.relationshipTypes)) {
        return DEFAULT_RELATIONSHIP_SIZE;
      }
      const relTypeDetails = style?.relationshipTypes.find(({ id }) => id === rel.type) ?? {
        size: DEFAULT_RELATIONSHIP_SIZE,
        styleRules: undefined,
      };
      const size = relTypeDetails?.size ?? DEFAULT_RELATIONSHIP_SIZE;
      const override = getRuleBasedSize(relTypeDetails?.styleRules, rel);
      return override !== undefined ? override : size;
    },
);

export const selectIconForNodeMapper = createSelector(
  selectCategoryStyleForNode,
  (mapper) =>
    (node: BloomNode): NvlNode['icon'] => {
      const cat = mapper(node);
      return cat?.icon;
    },
);

export const selectColorForNodeMapper = createSelector(
  selectCategoryStyleForNode,
  (mapper) =>
    (node: BloomNode): NvlNode['color'] => {
      const category = mapper(node);
      const color = category.color || DEFAULT_UNCATEGORISED_COLOR;
      const override = getRuleBasedColor(category.styleRules, node, node.labels);
      return override !== undefined ? override : color;
    },
);

export const selectCaptionSizeForNodeMapper = createSelector(
  selectCategoryStyleForNode,
  (mapper) =>
    (node: BloomNode): NvlNode['captionSize'] => {
      const category = mapper(node);

      if ('id' in category && category.id === DEFAULT_UNCATEGORISED_ID) {
        return DEFAULT_UNCATEGORISED_SIZE;
      }

      const override = getRuleBasedTextSize(category.styleRules, node, node.labels);
      return override !== undefined ? override : category.textSize;
    },
);

export const selectCaptionAlignForNodeMapper = createSelector(
  selectCategoryStyleForNode,
  (mapper) =>
    (node: BloomNode): NvlNode['captionAlign'] => {
      const category = mapper(node);

      if ('id' in category && category.id === DEFAULT_UNCATEGORISED_ID) {
        return DEFAULT_UNCATEGORISED_TEXT_ALIGN;
      }

      const alignmentOverride = getRuleBasedTextAlign(category.styleRules, node, node.labels);
      const alignmentSetting = alignmentOverride ?? category.textAlign;

      // if there is no icon, text is always in the middle
      return category.icon === 'no-icon' || isEmpty(category.icon) ? 'center' : alignmentSetting;
    },
);

export const selectCaptionsForNodeMapper = createSelector(
  (state: RootState) => selectCategoryStyleForNode(state),
  (state: RootState) => getCategoryForNode(state),
  (categoryStyleForNodeMapper, categoryForNodeMapper) =>
    (node: BloomNode): NvlNode['captions'] => {
      const category = categoryStyleForNodeMapper(node);
      const override = getRuleBasedCaptions(category.styleRules, node, node.labels);

      if ('id' in category && category.id === DEFAULT_UNCATEGORISED_ID) {
        return [
          {
            styles: [],
            value: `<ID> ${node.id}`,
          },
        ];
      }

      // Default caption if no configuration selected
      if (isNil(override) && isNil(category?.captions)) {
        const categoryForNode = categoryForNodeMapper(node);
        const propertyKeys = categoryForNode.properties
          .filter((property) => !property.exclude)
          .map((property) => property.name);
        const captionPropertyKey = findCaptionProperty(propertyKeys);
        const propertyValue =
          captionPropertyKey !== null ? node.mappedProperties?.[captionPropertyKey]?.value : undefined;

        return [
          {
            styles: [],
            value: typeof propertyValue === 'string' ? propertyValue : '',
          },
        ];
      }

      return (override !== undefined ? override : (category?.captions ?? [])).reduce(
        (acc: { styles: CaptionStyle[]; value?: string }[], a: Caption) => {
          if (a.isCaption) {
            const propertyValue =
              a.type === 'property'
                ? !isNil(a.key) && !isNil(node?.mappedProperties?.[a.key])
                  ? `${node.mappedProperties?.[a.key]?.value}`
                  : ''
                : a.key;
            if (a.type === 'label' && !node.labels.includes(a.key as string)) {
              acc = [...acc];
            } else if (propertyValue?.length !== 0) {
              acc = [...acc, { styles: a.styles, value: propertyValue }];
            }
          }
          return acc;
        },
        [],
      );
    },
);

export const selectCaptionForNodeMapper = createSelector(
  selectCategoryStyleForNode,
  getCategoryForNode,
  (categoryStyleForNodeMapper, categoryForNodeMapper) => (node: BloomNode) => {
    const category = categoryStyleForNodeMapper(node);
    const override = getRuleBasedCaptions(category.styleRules, node, node.labels);

    if ('id' in category && category.id === DEFAULT_UNCATEGORISED_ID) {
      return `<ID> ${node.id}`;
    }

    // Default caption if no configuration selected
    if (isNil(override) && isNil(category?.captions)) {
      const categoryForNode = categoryForNodeMapper(node);
      const propertyKeys = categoryForNode.properties
        .filter((property) => !property.exclude)
        .map((property) => property.name);
      const captionPropertyKey = findCaptionProperty(propertyKeys);
      const propertyValue =
        captionPropertyKey !== null ? node.mappedProperties?.[captionPropertyKey]?.value : undefined;

      return propertyValue;
    }

    const captions: Caption[] = (override !== undefined ? override : (category?.captions ?? [])).reduce(
      (acc: { styles: CaptionStyle[]; value?: string }[], a: Caption) => {
        if (a.isCaption) {
          const propertyValue =
            a.type === 'property'
              ? !isNil(a.key) && !isNil(node?.mappedProperties?.[a.key])
                ? `${node.mappedProperties?.[a.key]?.value}`
                : ''
              : a.key;
          if (a.type === 'label' && !node.labels.includes(a.key as string)) {
            acc = [...acc];
          } else if (propertyValue?.length !== 0) {
            acc = [...acc, { styles: a.styles, value: propertyValue }];
          }
        }
        return acc;
      },
      [],
    );

    return captions.map(({ value }) => value).join(', ');
  },
);

export const selectCaptionSizeForRelationshipMapper = createSelector(
  selectCurrentStyle,
  (style) =>
    (rel: BloomRelationship): NvlRelationship['width'] => {
      const relTypeStyles = style?.relationshipTypes?.find(({ id }) => id === rel.type);

      if (isNil(style?.relationshipTypes) || isNil(relTypeStyles)) {
        return DEFAULT_UNCATEGORISED_TEXT_SIZE;
      }

      const override = getRuleBasedTextSize(relTypeStyles?.styleRules, rel);
      return override !== undefined ? override : relTypeStyles.textSize;
    },
);

export const selectCaptionAlignForRelationshipMapper = createSelector(
  selectCurrentStyle,
  (style) =>
    (rel: BloomRelationship): NvlRelationship['captionAlign'] => {
      const relTypeStyles = style?.relationshipTypes?.find(({ id }) => id === rel.type);

      if (isNil(style) || isNil(style.relationshipTypes) || isNil(relTypeStyles)) {
        return DEFAULT_UNCATEGORISED_TEXT_ALIGN_RELATIONSHIP;
      }

      const override = getRuleBasedTextAlign(relTypeStyles.styleRules, rel) as 'center' | 'top' | 'bottom' | undefined;
      return override ?? (relTypeStyles.textAlign as 'center' | 'top' | 'bottom');
    },
);

export const selectCaptionsForRelationshipMapper = createSelector(
  selectCurrentStyle,
  getRelationshipTypes,
  (style, relationshipTypes) => {
    const isVirtualRelationship = isVirtualRelationshipFactory(relationshipTypes);
    return (rel: BloomRelationship): NvlRelationship['captions'] => {
      if (isNil(style) || isNil(style?.relationshipTypes)) {
        return [];
      }

      if (isVirtualRelationship(rel)) {
        return [
          {
            styles: [],
            value: rel.type,
          },
        ];
      }

      const relTypeStyles = style.relationshipTypes.find(({ id }) => id === rel.type);
      if (isNil(relTypeStyles)) return [];
      const override: Caption[] | undefined = getRuleBasedCaptions(relTypeStyles.styleRules, rel);

      if (isNil(override) && isNil(relTypeStyles?.captions)) {
        return [
          {
            styles: [],
            value: rel.type,
          },
        ];
      }

      return (override ?? relTypeStyles?.captions ?? []).reduce((acc: NvlRelationship['captions'] = [], a: Caption) => {
        if (a.isCaption) {
          const propertyValue =
            a.type === 'property'
              ? !isNil(a.key) && !isNil(rel?.mappedProperties?.[a?.key])
                ? `${rel.mappedProperties?.[a.key]?.value}`
                : ''
              : a.key;

          if (!isNil(propertyValue)) {
            const styles = a.styles as [];
            acc = [...acc, { styles, value: propertyValue }];
          }
        }
        return acc;
      }, []);
    };
  },
);

export const selectCaptionForRelationshipMapper = createSelector(
  selectCurrentStyle,
  getRelationshipTypes,
  (style, relationshipTypes) => {
    const isVirtualRelationship = isVirtualRelationshipFactory(relationshipTypes);
    return (rel: BloomRelationship): string => {
      if (isNil(style) || isNil(style?.relationshipTypes)) {
        return '';
      }

      if (isVirtualRelationship(rel)) {
        return rel.type;
      }

      const relTypeStyles = style.relationshipTypes.find(({ id }) => id === rel.type);
      if (isNil(relTypeStyles)) return '';
      const override = getRuleBasedCaptions(relTypeStyles.styleRules, rel);

      if (isNil(override) && isNil(relTypeStyles?.captions)) {
        return rel.type;
      }

      const captions = (override !== undefined ? override : (relTypeStyles?.captions ?? [])).reduce(
        (
          acc: {
            value: string;
            styles: CaptionStyle[];
          }[],
          a: Caption,
        ) => {
          if (a.isCaption) {
            const propertyValue =
              a.type === 'property'
                ? !isNil(a.key) && !isNil(rel?.mappedProperties?.[a?.key])
                  ? `${rel.mappedProperties?.[a.key]?.value}`
                  : ''
                : a.key;

            if (!isNil(propertyValue)) {
              acc = [...acc, { styles: a.styles, value: propertyValue }];
            }
          }
          return acc;
        },
        [],
      );

      return captions.map(({ value }: { value: string }) => value).join(', ');
    };
  },
);

export const selectUniqueValuesForStyleRules = createSelector(
  selectCategoryStyleForNode,
  (mapper) => (nodes: BloomNode[]) => {
    const result: Record<string, Partial<StyleRule>> = {};

    for (const node of nodes) {
      const category = mapper(node);
      const colorByValueRules: StyleRule[] | undefined = category?.styleRules?.filter(
        (rule: StyleRule) => rule.type === 'unique-values',
      );

      if (!isNil(colorByValueRules) && colorByValueRules?.length > 0) {
        colorByValueRules.forEach((colorByValueRule) => {
          const property = getTitleFromRuleBase(colorByValueRule.basedOn);

          result[colorByValueRule.id] = result[colorByValueRule.id] ?? { valuesMapper: [], existingValues: [] };
          const singleRule = result[colorByValueRule.id];

          if (singleRule === undefined) return;
          if (singleRule.valuesMapper && singleRule.valuesMapper.length >= COLOR_MAPPING_THRESHOLD) {
            const value = singleRule.valuesMapper[singleRule.valuesMapper.length - 1];
            if (!value) return;
            value.hasMore = true;
          } else {
            const nodeValue = node.mappedProperties?.[property]?.value;
            if (
              !isNil(nodeValue) &&
              singleRule.existingValues &&
              singleRule.valuesMapper &&
              !singleRule.existingValues.includes(nodeValue as string)
            ) {
              const color = fullPallete[singleRule.existingValues.length % fullPallete.length];
              color !== undefined &&
                singleRule.valuesMapper.push({
                  value: nodeValue,
                  color,
                  hasMore: false,
                });
              singleRule.existingValues.push(nodeValue as string);
            }
          }
        });
      }
    }
    return result;
  },
);

export const selectSizeForNodeMapper = createSelector(
  selectCategoryStyleForNode,
  (mapper) =>
    (node: BloomNode): NvlNode['size'] => {
      const category = mapper(node);
      const size = category.size || DEFAULT_UNCATEGORISED_SIZE;
      const override = getRuleBasedSize(category.styleRules, node, node.labels);
      return override ?? size;
    },
);

export const selectColorForCategoryMapper = createSelector(
  selectCurrentStyle,
  (style) => (categoryId: Category['id']) => {
    const category = style?.categories?.find(({ id }) => id === categoryId);
    return category?.color ?? DEFAULT_UNCATEGORISED_COLOR;
  },
);

export const selectCaptionsFromCategoryMapper = createSelector(
  selectCurrentStyle,
  (style) => (categoryId: Category['id']) => {
    if (isNil(style)) return [];
    const category = style?.categories?.find(({ id }) => id === categoryId);
    return category?.captions ?? [];
  },
);

export const selectCaptionsFromRelationshipMapper = createSelector(selectCurrentStyle, (style) => (relType: string) => {
  if (isNil(style)) return [];
  const relationship = style?.relationshipTypes?.find(({ id }) => id === relType);
  return relationship?.captions ?? [];
});

export const selectColorForCategoryMapperByName = createSelector(
  selectCurrentStyle,
  getCategoryByName,
  (style, categoryMapper) => (categoryName: Category['name']) => {
    const category = categoryMapper(categoryName);
    if (isNil(style)) return DEFAULT_UNCATEGORISED_COLOR;
    const styleCategory = style?.categories?.find(({ id }) => id === category?.id);
    return styleCategory?.color ?? DEFAULT_UNCATEGORISED_COLOR;
  },
);

export const selectStyleRulesForCategory = (state: RootState, catId: Category['id']) => {
  const style = selectCurrentStyle(state);
  if (isNil(style)) return undefined;
  const styleCategory = style?.categories?.find(({ id }: { id: StyleCategory['id'] }) => id === catId);
  return styleCategory?.styleRules;
};
